import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  TextField,
  InputAdornment,
  Paper,
  Divider,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Loop as LoopIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Gavel as LawIcon,
} from '@mui/icons-material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTaskTemplates } from '../hooks/useTaskTemplates';
import PageHeader from '../../../components/common/PageHeader';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Utility function to build tree structure from dot-notation categories
function buildCategoryTree(categories) {
  const tree = {};

  categories.forEach(category => {
    const parts = category.split('.');
    let current = tree;

    parts.forEach((part, index) => {
      if (!current[part]) {
        current[part] = {
          id: parts.slice(0, index + 1).join('.'),
          label: part,
          fullPath: parts.slice(0, index + 1).join('.'),
          children: {},
          isLeaf: index === parts.length - 1
        };
      }
      current = current[part].children;
    });
  });

  return tree;
}

// Convert tree object to array format for rendering
function treeToArray(tree) {
  return Object.values(tree).map(node => ({
    ...node,
    children: Object.keys(node.children).length > 0 ? treeToArray(node.children) : []
  }));
}

export default function TaskCatalogPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  // Set by flows like onboarding that want the user sent back after creating
  const returnTo = useLocation().state?.returnTo;
  const { t } = useTranslation(["tasks", "common"]);

  const {
    templates,
    templatesByCategory,
    categories,
    loading,
    error: apiError,
    fetchAllTemplates,
    searchTemplates,
    clearError,
  } = useTaskTemplates();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedTreeItems, setExpandedTreeItems] = useState([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load templates on mount
  useEffect(() => {
    fetchAllTemplates();
  }, [fetchAllTemplates]);

  // Set initial category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  // Handle search
  useEffect(() => {
    if (debouncedSearch) {
      searchTemplates(debouncedSearch);
    } else {
      fetchAllTemplates();
    }
  }, [debouncedSearch, searchTemplates, fetchAllTemplates]);

  // Build category tree structure
  const categoryTree = useMemo(() => {
    const tree = buildCategoryTree(categories);
    return treeToArray(tree);
  }, [categories]);

  // Get currently displayed tasks (exact category match only)
  const categoryTasks = useMemo(() => {
    if (!selectedCategory) return [];
    return templatesByCategory[selectedCategory] || [];
  }, [templatesByCategory, selectedCategory]);

  // Auto-select first available category when current one is hidden by search
  useEffect(() => {
    if (debouncedSearch && selectedCategory) {
      const hitCount = templatesByCategory[selectedCategory]?.length || 0;
      if (hitCount === 0 && categories.length > 0) {
        // Find first category with results
        const firstCategoryWithResults = categories.find(
          (key) => (templatesByCategory[key]?.length || 0) > 0
        );
        if (firstCategoryWithResults) {
          setSelectedCategory(firstCategoryWithResults);
        }
      }
    }
  }, [debouncedSearch, selectedCategory, templatesByCategory, categories]);

  const handleCategoryChange = (event, itemId) => {
    setSelectedCategory(itemId);
    setExpandedCards({});
  };

  const handleTreeItemExpansionToggle = (event, itemIds) => {
    setExpandedTreeItems(itemIds);
  };

  const handleRefresh = () => {
    fetchAllTemplates();
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTask(null);
  };

  const handleUseTemplate = (task) => {
    // Navigate to create task page with template data in state
    navigate('/tasks/create', {
      state: {
        templateTask: task,
        returnTo,
      }
    });
    handleCloseDetails();
  };

  const toggleCardExpansion = (taskId) => {
    setExpandedCards(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const getCategoryLabel = (key) => {
    const lastPart = key.split('.').pop();
    const translated = t(`tasks:catalog.categories.${lastPart}`, { defaultValue: null });
    if (translated) return translated;

    return lastPart.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Recursive function to render tree items
  const renderTreeItems = (nodes) => {
    return nodes.map((node) => {
      const taskCount = templatesByCategory[node.fullPath]?.length || 0;

      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          label={
            <Box display="flex" alignItems="center" justifyContent="space-between" py={0.5}>
              <Typography variant="body2">
                {getCategoryLabel(node.label)}
              </Typography>
              {taskCount > 0 && (
                <Chip
                  label={taskCount}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>
          }
        >
          {node.children.length > 0 && renderTreeItems(node.children)}
        </TreeItem>
      );
    });
  };

  const getFrequencyLabel = (task) => {
    if (!task.is_recurring) return t('tasks:catalog.frequency.one_time');

    const { frequency_interval, unit } = task;

    if (frequency_interval === 1) {
      return t(`tasks:catalog.frequency.every_singular.${unit}`);
    }

    return t(`tasks:catalog.frequency.every_plural`, {
      interval: frequency_interval,
      unit: t(`tasks:catalog.frequency.units.${unit}`)
    });
  };

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={() => navigateBack('/tasks')}
        title={t('tasks:catalog.title')}
        subtitle={t('tasks:catalog.subtitle')}
        actions={<>
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </>}
      />

      {/* Search Bar */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder={t('tasks:catalog.search.placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{ htmlInput: { autoFocus: true } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {apiError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {apiError}
        </Alert>
      )}

      {/* Two-Column Layout: Tree View + Task Cards */}
      {categories.length > 0 && (
        <Box display="flex" gap={3} minHeight="calc(100vh - 300px)">
          {/* Left Column - Category Tree */}
          <Paper
            elevation={2}
            sx={{
              width: 320,
              flexShrink: 0,
              p: 2,
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              position: 'sticky',
              top: 20,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              {t('tasks:catalog.categories_title', { defaultValue: 'Categories' })}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <SimpleTreeView
              selectedItems={selectedCategory}
              onSelectedItemsChange={handleCategoryChange}
              expandedItems={expandedTreeItems}
              onExpandedItemsChange={handleTreeItemExpansionToggle}
              sx={{
                '& .MuiTreeItem-content': {
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                    '&:hover': {
                      backgroundColor: 'action.focus',
                    },
                  },
                },
              }}
            >
              {renderTreeItems(categoryTree)}
            </SimpleTreeView>
          </Paper>

          {/* Right Column - Task Cards */}
          <Box flex={1}>
            {/* Loading State */}
            {loading && (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            )}

            {/* Task Cards Grid */}
            {!loading && categoryTasks.length > 0 && (
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))"
                gap={3}
              >
                {categoryTasks.map((task) => (
                  <Card
                    key={task.template_id}
                    sx={{
                      height: 'fit-content',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      border: (task.has_tasks || task.law_requirement) ? '2px solid' : 'none',
                      borderColor: task.has_tasks ? 'success.main' : task.law_requirement ? 'error.main' : 'transparent',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTaskClick(task);
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
                          {task.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardExpansion(task.template_id);
                          }}
                        >
                          {expandedCards[task.template_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        {task.is_recurring && (
                          <Chip
                            icon={<LoopIcon />}
                            label={getFrequencyLabel(task)}
                            size="small"
                            color={
                              task.unit === "days"
                                ? "secondary"
                                : task.unit === "weeks"
                                  ? "success"
                                  : task.unit === "months"
                                    ? "warning"
                                    : "info"
                            }
                            variant="outlined"
                          />
                        )}
                        {task.law_requirement && (
                          <Chip
                            icon={<LawIcon />}
                            label={t('tasks:catalog.law_requirement')}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          icon={<CategoryIcon />}
                          label={getCategoryLabel(selectedCategory)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {task.version > 1 && (
                          <Chip
                            label={`v${task.version}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Collapse in={expandedCards[task.template_id]} timeout="auto">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: 'pre-line',
                            mb: 2
                          }}
                        >
                          {task.description}
                        </Typography>
                      </Collapse>

                      {!expandedCards[task.template_id] && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 2
                          }}
                        >
                          {task.description}
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTaskClick(task);
                        }}
                      >
                        {t('tasks:catalog.actions.view_details')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUseTemplate(task);
                        }}
                      >
                        {t('tasks:catalog.actions.use_template')}
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            )}

            {/* Empty State */}
            {!loading && categoryTasks.length === 0 && selectedCategory && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
              >
                <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {debouncedSearch ? t('tasks:catalog.empty.no_search_results') : t('tasks:catalog.empty.no_tasks')}
                </Typography>
                {debouncedSearch && (
                  <Button onClick={handleClearSearch} sx={{ mt: 2 }}>
                    {t('tasks:catalog.search.clear')}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Task Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            border: (selectedTask?.has_tasks || selectedTask?.law_requirement) ? '2px solid' : 'none',
            borderColor: selectedTask?.has_tasks ? 'success.main' : selectedTask?.law_requirement ? 'error.main' : 'transparent',
          }
        }}
      >
        <DialogTitle>
          {selectedTask?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box>
              <Box mb={3}>
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  {selectedTask.is_recurring && (
                    <Chip
                      icon={<LoopIcon />}
                      label={getFrequencyLabel(selectedTask)}
                      color={
                        selectedTask.unit === "days"
                          ? "secondary"
                          : selectedTask.unit === "weeks"
                            ? "success"
                            : selectedTask.unit === "months"
                              ? "warning"
                              : "info"
                      }
                      variant="outlined"
                    />
                  )}
                  {selectedTask.law_requirement && (
                    <Chip
                      icon={<LawIcon />}
                      label={t('tasks:catalog.law_requirement')}
                      color="error"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    icon={<CategoryIcon />}
                    label={getCategoryLabel(selectedCategory)}
                    color="primary"
                    variant="outlined"
                  />
                  {selectedTask.version > 1 && (
                    <Chip
                      label={`Version ${selectedTask.version}`}
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {selectedTask.skip_closing_days && (
                    <Chip
                      label={t('tasks:catalog.dialog.skip_closing_days')}
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                {t('common:labels.description')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ whiteSpace: 'pre-line' }}
              >
                {selectedTask.description}
              </Typography>

              {selectedTask.is_recurring && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    {t('tasks:catalog.dialog.recurrence_details')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tasks:catalog.dialog.frequency')}: {getFrequencyLabel(selectedTask)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            {t('common:buttons.close')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleUseTemplate(selectedTask)}
          >
            {t('tasks:catalog.actions.use_template')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
